import { useEffect, useMemo, useState } from "react"

import { FlexContainer } from "../../../../styled/flexbox.styled"
import CollapsibleTagList from "../../../components/CollapsibleTagList"
import useTradingCountries from "../hooks/useTradingCountries"
import useTradingRegions from "../hooks/useTradingRegions"
import { DatasetFilters } from "./types"

interface IGeoFilterProps {
  filters: DatasetFilters
  onFilterChange: (filters: {
    regionIdParam?: number
    subRegionIdParam?: number
    countryCodeParam?: string[]
  }) => void
}

const GeoFilter = ({ filters, onFilterChange }: IGeoFilterProps) => {
  const { regions } = useTradingRegions()
  const { countries } = useTradingCountries()
  const subRegions = useMemo(() => regions?.flatMap(region => region.sub_regions) ?? [], [regions])

  useEffect(() => {
    onFilterChange({
      regionIdParam: filters.regionIdParam,
      subRegionIdParam: filters.subRegionIdParam,
      countryCodeParam: filters.countryCodeParam,
    })
  }, [filters, onFilterChange])

  return (
    <FlexContainer flexDirection="column" gap="10px">
      <CollapsibleTagList
        title="Regions"
        items={regions ?? []}
        selectedItemIds={filters.regionIdParam ? [filters.regionIdParam] : []}
        onItemToggle={item => {
          if (filters.regionIdParam === item.id) {
            onFilterChange({ regionIdParam: undefined })
          } else {
            onFilterChange({ regionIdParam: item.id })
          }
        }}
        initialCollapsed={true}
        onClear={() => onFilterChange({ regionIdParam: undefined })}
      />
      <CollapsibleTagList
        title="Sub-regions"
        items={subRegions}
        selectedItemIds={filters.subRegionIdParam ? [filters.subRegionIdParam] : []}
        onItemToggle={item => {
          if (filters.subRegionIdParam === item.id) {
            onFilterChange({ subRegionIdParam: undefined })
          } else {
            onFilterChange({ subRegionIdParam: item.id })
          }
        }}
        initialCollapsed={true}
        onClear={() => onFilterChange({ subRegionIdParam: undefined })}
      />
      <CollapsibleTagList
        title="Countries"
        items={countries ?? []}
        selectedItemIds={
          filters.countryCodeParam
            ? filters.countryCodeParam
                .map(code => countries?.find(country => country.code === code)?.id)
                .filter((id): id is number => id !== undefined)
            : []
        }
        onItemToggle={item => {
          if (filters.countryCodeParam?.includes(item.code)) {
            onFilterChange({
              countryCodeParam: filters.countryCodeParam?.filter(
                countryCode => countryCode !== item.code,
              ),
            })
          } else {
            onFilterChange({
              countryCodeParam: [...(filters.countryCodeParam ?? []), item.code],
            })
          }
        }}
        initialCollapsed={true}
        onClear={() => onFilterChange({ countryCodeParam: undefined })}
      />
    </FlexContainer>
  )
}

export default GeoFilter
